import {
    Popover,
    PopoverTrigger,
    PopoverContent,
    Button,
    Box,
    Text,
    useDisclosure,
    Portal,
    Flex,
    VStack,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement
} from "@chakra-ui/react";
import React, { useState, useEffect } from 'react';
import { calculateDateRange, getPolishMonthName } from '../helpers/DateHelpers';
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import pl from "date-fns/locale/pl";
import moment from "moment";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";

registerLocale("pl", pl);

const DateRangeFilter = ({ onDateChange, dateRange }) => {
    const { t } = useTranslation("default");
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedLabel, setSelectedLabel] = useState(t('date.label'));
    const [showCustomCalendar, setShowCustomCalendar] = useState(false);

    const options = [
        { key: 'today', label: t('date.today') },
        { key: 'tomorrow', label: t('date.tomorrow') },
        { key: 'this_weekend', label: t('date.this_weekend') },
        { key: 'next_week', label: t('date.next_week') },
        { key: 'this_month', label: t('date.this_month') },
        { key: 'custom', label: t('date.custom') }
    ];

    const isDateSelected = !!(dateRange[0]);

    const handleClear = (e) => {
        e.stopPropagation();
        onDateChange([null, null]);
        onClose();
        setShowCustomCalendar(false);
    };

    const handleOptionClick = (key) => {
        if (key === 'custom') {
            setShowCustomCalendar(true);
            // Do not close the popover, only change its content
        } else {
            const dates = calculateDateRange(key);
            if (dates) {
                onDateChange([dates.startDate, dates.endDate]);
            }
            setShowCustomCalendar(false); // Reset to show options list next time
            onClose(); // Close the popover for predefined options
        }
    };

    useEffect(() => {
        const [startDate, endDate] = dateRange;
        if (startDate && endDate) {
            const start = moment(startDate);
            const end = moment(endDate);
            if (start.isSame(end, 'day')) {
                if (start.isSame(moment(), 'day')) {
                    setSelectedLabel(t('date.today'));
                } else if (start.isSame(moment().add(1, 'days'), 'day')) {
                    setSelectedLabel(t('date.tomorrow'));
                } else {
                    setSelectedLabel(`${start.date()} ${getPolishMonthName(start.month())}`);
                }
            } else {
                setSelectedLabel(`${start.date()}.${start.format('MM')} - ${end.date()}.${end.format('MM')}`);
            }
        } else {
            setSelectedLabel(t('date.label'));
        }
    }, [dateRange]);


    return (
        <Box flex="1">
            <Popover
                isOpen={isOpen}
                onOpen={onOpen}
                onClose={() => {
                    onClose();
                    setShowCustomCalendar(false); // Reset on close
                }}
                placement="bottom-start"
                isLazy
            >
                <PopoverTrigger>
                    <Box w="100%" cursor="pointer" onClick={(e) => {
                        // Let Chakra handle the Popover toggle via trigger
                    }}>
                        <InputGroup flex="1">
                            <InputLeftElement pointerEvents="none">
                                <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5.16667 4.33333V1M11.8333 4.33333V1M4.33333 7.66667H12.6667M2.66667 16H14.3333C14.7754 16 15.1993 15.8244 15.5118 15.5118C15.8244 15.1993 16 14.7754 16 14.3333V4.33333C16 3.89131 15.8244 3.46738 15.5118 3.15482C15.1993 2.84226 14.7754 2.66667 14.3333 2.66667H2.66667C2.22464 2.66667 1.80072 2.84226 1.48816 3.15482C1.17559 3.46738 1 3.89131 1 4.33333V14.3333C1 14.7754 1.17559 15.1993 1.48816 15.5118C1.80072 15.8244 2.22464 16 2.66667 16Z" stroke="#787878" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </InputLeftElement>
                            <Input
                                border="0px"
                                borderRadius="0"
                                value={selectedLabel === t('date.label') ? "" : selectedLabel}
                                placeholder={t('date.label')}
                                readOnly
                                cursor="pointer"
                                bg="transparent"
                                fontFamily="'DM Sans', sans-serif"
                                fontSize={{ base: '16px', md: '15px' }}
                                fontWeight="400"
                                lineHeight="normal"
                                color="#787878"
                                _placeholder={{ color: '#787878' }}
                                sx={{ '--input-font-size': { base: '16px', md: '15px' } }}
                                _focusVisible={{ shadow: "none" }}
                                _focus={{ shadow: "none" }}
                                _hover={{ bg: 'transparent' }}
                                pl={10}
                            />
                            <InputRightElement pointerEvents={isDateSelected ? 'auto' : 'none'}>
                                {isDateSelected ? (
                                    <Box
                                        as="button"
                                        onClick={handleClear}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        w="18px"
                                        h="18px"
                                        borderRadius="full"
                                        bg="gray.200"
                                        color="#555"
                                        fontSize="13px"
                                        lineHeight="1"
                                        _hover={{ bg: 'gray.300' }}
                                        transition="background 0.15s"
                                        aria-label={t('date.clear')}
                                    >
                                        ×
                                    </Box>
                                ) : (
                                    <ChevronDownIcon color="#787878" />
                                )}
                            </InputRightElement>
                        </InputGroup>
                    </Box>
                </PopoverTrigger>
                <Portal>
                    <PopoverContent zIndex={400} p={0} w="auto" border="none" shadow="lg" rounded="md">
                        {!showCustomCalendar ? (
                            <VStack align="stretch" spacing={1} p={2}>
                                {options.map(option => (
                                    <Flex
                                      key={option.key}
                                      as="button"
                                      w="100%"
                                      textAlign="left"
                                      align="center"
                                      gap={3}
                                      p={3}
                                      borderRadius="md"
                                      _hover={{ bg: 'gray.100', cursor: 'pointer' }}
                                      onClick={() => handleOptionClick(option.key)}
                                    >
                                      <Text noOfLines={1} fontWeight="medium" color="#787878" fontFamily="body" fontSize="18px">{option.label}</Text>
                                    </Flex>
                                ))}
                            </VStack>
                        ) : (
                            <DatePicker
                                selectsRange
                                startDate={dateRange[0]}
                                endDate={dateRange[1]}
                                onChange={(update) => {
                                    onDateChange(update);
                                    if (update[0] && update[1]) {
                                        onClose();
                                        setShowCustomCalendar(false);
                                    }
                                }}
                                inline
                                locale="pl"
                                showMonthDropdown
                                showYearDropdown
                            />
                        )}
                    </PopoverContent>
                </Portal>
            </Popover>
        </Box>
    );
};

export default DateRangeFilter;